import { useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import type { Company, CompanyListFilter, Query } from "@/graphql/codegen/graphql"
import { CompanyBaseListDocument, CompanyBaseWithLocationsDocument } from "@/graphql/codegen/graphql"

export interface UseGetBaseCompaniesReturn {
  companies: Company[]
  isFetchingCompanies: boolean
  companiesError: CombinedError | undefined
  loadMoreCompanies: () => void
  getCompaniesWithReset: () => void
  resetCompanies: () => void
}

export const useGetBaseCompanies = (
  filter: CompanyListFilter = {},
  withLocations = false,
  limit = 20
): UseGetBaseCompaniesReturn => {
  const [baseCompanies, setBaseCompanies] = useState<Company[]>([])
  const [offset, setOffset] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<Pick<Query, "company">>({
    query: withLocations ? CompanyBaseWithLocationsDocument : CompanyBaseListDocument,
    variables: { input: { filter, limit, offset } },
  })

  useEffect(() => {
    if (data?.company.list.__typename === "CompanyListSuccess") {
      const paginatedCompanies = data.company.list.companies
      setBaseCompanies((prev: Company[]) => (offset === 0 ? paginatedCompanies : [...prev, ...paginatedCompanies]))

      if (paginatedCompanies.length < limit) {
        setHasNextPage(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const loadMoreCompanies = () => {
    if (hasNextPage) {
      setOffset((prevOffset) => prevOffset + limit)
    }
  }

  const resetCompanies = () => {
    setOffset(0)
    setBaseCompanies([])
    setHasNextPage(true)
  }

  const getCompaniesWithReset = () => {
    resetCompanies()
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return {
    companies: baseCompanies,
    isFetchingCompanies: fetching,
    companiesError: error,
    loadMoreCompanies,
    getCompaniesWithReset,
    resetCompanies,
  }
}
